import { Separator, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@bpinternal/ui-kit-next'
import { useSuspenseInfiniteQuery, useSuspenseQueries } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { Fragment, useCallback } from 'react'
import { Avatar, Icon } from '~/elements'
import { StatusBadge } from '~/features/hitl/components/StatusBadge'
import {
  listConversationUsersQueryOptions,
  getIntegrationByNameQueryOptions,
  listHitlMessagesInfiniteQueryOptions,
} from '~/queries'
import { getQueryOptions } from '~/services'
import { cn } from '~/utils'
import { isConversationStatus } from '../types'
import { Video, Image, FileText, Headphones, MapPin } from 'lucide-react'
import { narrowTimeDelta } from '@bpinternal/shared'
import { ExtendedBridgeMessage } from '~/types'
import { Conversation } from '@botpress/hitl-client'

type ConversationItemProps = {
  botId: string
  workspaceId: string
  conversation: Conversation
  index: number
  selected: boolean
}

export const ConversationItem = ({ botId, workspaceId, conversation, index, selected }: ConversationItemProps) => {
  const downstreamConversationId = conversation.id

  const [
    { data: workspaceMembers },
    { data: integration },
    {
      data: { users },
    },
  ] = useSuspenseQueries({
    queries: [
      getQueryOptions('workspaces_/$workspaceId_/members', { workspaceId }),
      getIntegrationByNameQueryOptions({ name: 'hitl' }),
      listConversationUsersQueryOptions({
        conversationId: downstreamConversationId,
        botId,
        workspaceId,
      }),
    ],
  })

  const patientMessagesQuery = useSuspenseInfiniteQuery(
    listHitlMessagesInfiniteQueryOptions({
      conversationId: downstreamConversationId,
      botId,
      workspaceId,
    })
  )

  const searchParams = useSearch({ strict: false })

  const hitlStatus = isConversationStatus(conversation.status) ? conversation.status : 'unknown'

  const patient = users?.find((user) => user.id === conversation.requester?.id)
  const patientName = patient?.name === 'Unknown User' ? 'Anonymous User' : (patient?.name ?? 'Anonymous User')
  const lastPatientMessage = patientMessagesQuery.data.pages?.[0]?.messages?.[0]

  const assigneeId = conversation.assignee?.workspaceMemberId
  const assignee = workspaceMembers?.find((member) => member.id === assigneeId)

  const iconRenderer = useCallback(
    (props: { className?: string }) => <img src={integration.iconUrl} {...props} />,
    [integration.iconUrl]
  )

  return (
    <TooltipProvider>
      <Fragment key={conversation.id}>
        {index !== 0 && <Separator className="my-1" />}
        <Link
          to="/workspaces/$workspaceId/bots/$botId/hitl"
          className={cn('rounded-md p-3', selected ? 'bg-neutral-100' : 'hover:bg-neutral-50')}
          params={{
            botId,
            workspaceId,
          }}
          search={{ ...searchParams, conversationId: conversation.id }}
          preload={false}
        >
          <div className="flex gap-2 text-sm">
            <Avatar name={patientName} pictureUrl={patient?.pictureUrl} className="h-6 w-6" />
            <div className="flex min-w-0 flex-grow flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="truncate pr-1 font-medium">{patientName}</span>
                <div className="ml-auto flex items-center gap-1">
                  <StatusBadge status={hitlStatus} />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Icon size="2" variant={'surface'} color={'gray'} icon={iconRenderer} />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Conversation occurs on {integration.name}</p>
                    </TooltipContent>
                  </Tooltip>
                  {assignee && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Avatar
                          name={assignee.displayName}
                          pictureUrl={assignee.profilePicture}
                          className="h-5 w-5 rounded-sm"
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Assigned to {assignee.displayName ?? assignee.email}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
              {lastPatientMessage ? (
                <LastMessage message={lastPatientMessage} />
              ) : (
                <div className="flex items-center gap-1">
                  <p className="max-w-[80%] truncate text-sm font-light italic text-neutral-600">No last messages</p>
                </div>
              )}
            </div>
          </div>
        </Link>
      </Fragment>
    </TooltipProvider>
  )
}

const LastMessage = ({ message }: { message: ExtendedBridgeMessage }) => {
  let value
  let icon

  switch (message.type) {
    case 'text':
      value = message.payload.text
      break
    case 'image':
      value = 'Sent an image'
      icon = <Icon color={'gray'} icon={Image}></Icon>
      break
    case 'video':
      value = 'Sent a video'
      icon = <Icon color={'gray'} icon={Video}></Icon>
      break
    case 'file':
      value = 'Sent a file'
      icon = <Icon color={'gray'} icon={FileText}></Icon>
      break
    case 'audio':
      value = 'Sent an audio clip'
      icon = <Icon color={'gray'} icon={Headphones}></Icon>
      break
    case 'location':
      value = 'Sent a location'
      icon = <Icon color={'gray'} icon={MapPin}></Icon>
      break
    case 'markdown':
      value = message.payload.markdown
      break
    case 'bloc':
      value = 'Sent multiple messages'
      break
    default:
      value = 'Sent a message'
      break
  }

  const timeDelta = narrowTimeDelta(message.createdAt)

  return (
    <div className="flex items-center gap-1">
      <p className="max-w-[80%] truncate text-sm font-light text-neutral-900">{value}</p>
      {icon}
      <p className="ml-auto text-sm font-light text-neutral-700">{timeDelta}</p>
    </div>
  )
}
